import React from "react";
import { styled } from "@mui/system";
import CrownIcon from "src/icons/CrownIcon";

const Wrapper = styled("div")(() => ({
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "flex-start",
}));

const ProgressBar = styled("div")(() => ({
  display: "flex",
  width: "100%",
  padding: "0px 20px",
  alignItems: "center",
  gap: "16px",
}));

const IconWrapper = styled("div")(() => ({
  display: "flex",
  width: "6px",
  height: "8px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
}));

interface ProgressLineProps {
  background: string;
}
const ProgressLine = styled("div")<ProgressLineProps>(({ background }) => ({
  display: "flex",
  paddingBottom: "0px",
  justifyContent: "center",
  alignItems: "center",
  flex: "1 0 0",
  width: "100%",
  height: "1px",
  background: background,
}));

const ProgressCircleChecked = styled("div")(() => ({
  width: "8px",
  height: "8px",
  flexShrink: 0,
  borderRadius: "100px",
  background: "#002035",
}));

const ProgressCircleUnchecked = styled("div")(() => ({
  width: "8px",
  height: "8px",
  flexShrink: 0,
  borderRadius: "100px",
  border: "1px solid #D1D4D6",
}));

const Label = styled("div")(() => ({
  display: "flex",
  width: "100%",
  padding: "12px 8px 0px 8px",
  alignItems: "flex-end",
  justifyContent: "space-between",
}));

interface TextProps {
  bold: boolean;
}
const Text = styled("div")<TextProps>(({ bold }) => ({
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: bold ? 600 : 400,
  lineHeight: "19px",
}));

interface RaFProgressBarProps {
  tier: "bronze" | "silver" | "gold" | "black";
}

/**
 * A referral program progress bar component to visualize user progress.
 * @param {("bronze" | "silver" | "gold" | "black")} props.tier - The tier of the user progress.
 */
const RaFProgressBar = (props: RaFProgressBarProps) => {
  const { tier } = props;

  // progress line colors
  const progressLineColor = "#F8F9F9";
  let silverColor = progressLineColor;
  let GoldColor = progressLineColor;
  let blackColor = progressLineColor;

  switch (tier) {
    case "silver":
      silverColor = "#0BEC9F";
      break;
    case "gold":
      silverColor = "#0BEC9F";
      GoldColor = "#0BEC9F";
      break;
    case "black":
      silverColor = "#0BEC9F";
      GoldColor = "#0BEC9F";
      blackColor = "#0BEC9F";
      break;
    default:
      break;
  }

  // progress icons
  let bronzeIcon = <ProgressCircleUnchecked />;
  let silverIcon = <ProgressCircleUnchecked />;
  let goldIcon = <ProgressCircleUnchecked />;
  let blackIcon = <ProgressCircleUnchecked />;

  switch (tier) {
    case "bronze":
      bronzeIcon = <LockIcon />;
      break;
    case "silver":
      bronzeIcon = <ProgressCircleChecked />;
      silverIcon = <LockIcon />;
      break;
    case "gold":
      bronzeIcon = <ProgressCircleChecked />;
      silverIcon = <ProgressCircleChecked />;
      goldIcon = <LockIcon />;
      break;
    case "black":
      bronzeIcon = <ProgressCircleChecked />;
      silverIcon = <ProgressCircleChecked />;
      goldIcon = <ProgressCircleChecked />;
      blackIcon = <CrownIcon height="11" width="12" />;
      break;
    default:
      break;
  }

  return (
    <Wrapper>
      {/* Progress bar */}
      <ProgressBar>
        {/* bronze */}
        <IconWrapper>{bronzeIcon}</IconWrapper>

        {/* silver */}
        <ProgressLine background={silverColor} />
        <IconWrapper>{silverIcon}</IconWrapper>

        {/* gold */}
        <ProgressLine background={GoldColor} />
        <IconWrapper>{goldIcon}</IconWrapper>

        {/* black */}
        <ProgressLine background={blackColor} />
        <IconWrapper>{blackIcon}</IconWrapper>
      </ProgressBar>

      {/* Tier labels */}
      <Label>
        <Text bold={tier === "bronze"}>Bronze</Text>
        <Text bold={tier === "silver"}>Silver</Text>
        <Text bold={tier === "gold"}>Gold</Text>
        <Text bold={tier === "black"}>Black</Text>
      </Label>
    </Wrapper>
  );
};

export default RaFProgressBar;

const LockIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="10" viewBox="0 0 8 10" fill="none">
      <path
        d="M6.33332 3.90918H1.66666C1.29848 3.90918 1 4.32359 1 4.8348V8.07445C1 8.58566 1.29848 9.00007 1.66666 9.00007H6.33332C6.7015 9.00007 6.99998 8.58566 6.99998 8.07445V4.8348C6.99998 4.32359 6.7015 3.90918 6.33332 3.90918Z"
        fill="black"
        stroke="black"
        strokeWidth="0.545453"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.45459 3.90908V2.61688C2.4542 2.21631 2.59767 1.82989 2.85715 1.53261C3.11663 1.23534 3.4736 1.04844 3.85876 1.00818C4.24393 0.967925 4.62982 1.07719 4.94151 1.31476C5.25319 1.55234 5.46845 1.90127 5.54549 2.29383"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
